import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { aiService } from "@/lib/ai-service";
import { useAuth } from "@/contexts/AuthContext";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Badge,
	Progress,
	Tooltip,
	TooltipTrigger,
	TooltipContent,
	TooltipProvider,
} from "@/components/primitives";
import {
	ArrowLeft,
	BarChart3,
	TrendingUp,
	TrendingDown,
	Calendar,
	Clock,
	Target,
	AlertTriangle,
	CheckCircle,
	Brain,
	MessageSquare,
	Activity,
	Info,
} from "lucide-react";
import {
	LineChart,
	Line,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	ReferenceLine,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import { GroupAnalysisSection } from "@/components/GroupAnalysisSection";
import { StructuredTeamAnalysis } from "@/components/StructuredTeamAnalysis";

interface Topic {
	id: string;
	title: string;
	description: string;
	created_at: string;
	prompt_creation?: string;
}

interface TopicUser {
	id: string;
	name: string;
	email: string;
	last_activity: string;
	reflections_count: number;
	checkins_completed: number;
	progress_score: number;
	engagement_level: "high" | "medium" | "low";
}

interface CheckInTrend {
	date: string;
	mood_average: number;
	mood_high: number;
	mood_low: number;
	mood_spread: number;
	stress_average: number;
	participation_rate: number;
	individual_moods: number[];
}

interface InsightsAnalysis {
	positiveObservations: Array<{
		observation: string;
		frequency: number;
		impact: string;
		signals: string[];
		context: string;
	}>;
	negativeObservations: Array<{
		observation: string;
		frequency: number;
		impact: string;
		signals: string[];
		context: string;
	}>;
	impactTrends: {
		overall: "improving" | "declining" | "stable";
		recentTrend: string;
		significantChanges: string[];
	};
	signalPatterns: Array<{
		signal: string;
		frequency: number;
		correlation: string;
		impact: string;
	}>;
	insights: {
		keyFindings: string[];
		progressIndicators: string[];
		concernAreas: string[];
	};
	recommendations: Array<{
		action: string;
		priority: "high" | "medium" | "low";
		reasoning: string;
		timeframe: string;
	}>;
}

export function TopicDetailPage() {
	const { topicId, groupId } = useParams<{
		topicId?: string;
		groupId?: string;
	}>();
	const navigate = useNavigate();
	const { user } = useAuth();
	const [topic, setTopic] = useState<Topic | null>(null);
	const [groupName, setGroupName] = useState<string | null>(null);
	const [users, setUsers] = useState<TopicUser[]>([]);
	const [loading, setLoading] = useState(true);
	const [topicAssignments, setTopicAssignments] = useState<any[]>([]);
	const [insightsAnalysis, setInsightsAnalysis] =
		useState<InsightsAnalysis | null>(null);
	const [insightsLoading, setInsightsLoading] = useState(false);

	const [weeklyStats, setWeeklyStats] = useState({
		checkinsCompleted: 0,
		possibleCheckins: 0,
		newReflections: 0,
		avgMood: 0,
		stressTrend: "Stable",
	});

	const [dailyTrends, setDailyTrends] = useState<CheckInTrend[]>([]);
	const [trendsLoading, setTrendsLoading] = useState(false);
	const [trendsAnalysis, setTrendsAnalysis] = useState<{
		mostPositiveDay: { date: string; avg: number } | null;
		mostNegativeDay: { date: string; avg: number } | null;
		highestSpreadDay: { date: string; spread: number } | null;
		weekdayAverages: Record<string, number>;
		trendDirection: "Improving" | "Worsening" | "Stable";
	}>({
		mostPositiveDay: null,
		mostNegativeDay: null,
		highestSpreadDay: null,
		weekdayAverages: {},
		trendDirection: "Stable",
	});

	useEffect(() => {
		const fetchLast28Days = async () => {
			if (!topicId || users.length === 0) {
				setDailyTrends([]);
				return;
			}

			try {
				setTrendsLoading(true);
				const userIds = users.map((u) => u.id);
				const now = new Date();
				const start = new Date(now.getTime() - 27 * 24 * 60 * 60 * 1000); // inclusive last 28 days
				const { data: checkins, error } = await supabase
					.from("daily_checkins")
					.select("id, user_id, general_feeling, created_at")
					.in("user_id", userIds)
					.gte("created_at", start.toISOString());

				if (error) {
					console.error("Error fetching 28-day checkins:", error);
					setDailyTrends([]);
					setTrendsLoading(false);
					return;
				}

				// Build map of date -> list of checkins
				const dayMap: Record<string, any[]> = {};
				for (let i = 0; i < 28; i++) {
					const d = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
					const key = d.toISOString().slice(0, 10);
					dayMap[key] = [];
				}

				(checkins || []).forEach((ci: any) => {
					const key = new Date(ci.created_at).toISOString().slice(0, 10);
					if (!dayMap[key]) dayMap[key] = [];
					dayMap[key].push(ci);
				});

				const trends: CheckInTrend[] = Object.keys(dayMap)
					.sort()
					.map((dateKey) => {
						const items = dayMap[dateKey] || [];
						const individual_moods = items.map((it: any) =>
							Number(it.general_feeling || 0),
						);
						const participation_rate =
							users.length > 0
								? Math.round(
										(new Set(items.map((it: any) => it.user_id)).size /
											users.length) *
											100,
									)
								: 0;
						const mood_average = individual_moods.length
							? individual_moods.reduce((s: number, v: number) => s + v, 0) /
								individual_moods.length
							: 0;
						const mood_high = individual_moods.length
							? Math.max(...individual_moods)
							: 0;
						const mood_low = individual_moods.length
							? Math.min(...individual_moods)
							: 0;
						const mood_spread = mood_high - mood_low;

						return {
							date: dateKey,
							mood_average: Number(mood_average.toFixed(2)),
							mood_high: Number(mood_high.toFixed(2)),
							mood_low: Number(mood_low.toFixed(2)),
							mood_spread: Number(mood_spread.toFixed(2)),
							stress_average: 0,
							participation_rate,
							individual_moods,
						} as CheckInTrend;
					});

				setDailyTrends(trends);

				// Analysis
				const nonEmpty = trends.filter((t) => t.individual_moods.length > 0);
				let mostPositiveDay = null;
				let mostNegativeDay = null;
				let highestSpreadDay = null;

				if (nonEmpty.length > 0) {
					mostPositiveDay = nonEmpty.reduce(
						(best, t) =>
							!best || t.mood_average > best.mood_average ? t : best,
						nonEmpty[0],
					);
					mostNegativeDay = nonEmpty.reduce(
						(best, t) =>
							!best || t.mood_average < best.mood_average ? t : best,
						nonEmpty[0],
					);
					highestSpreadDay = nonEmpty.reduce(
						(best, t) => (!best || t.mood_spread > best.mood_spread ? t : best),
						nonEmpty[0],
					);
				}

				// Weekday averages
				const weekdaySums: Record<number, { sum: number; count: number }> = {};
				trends.forEach((t) => {
					const d = new Date(t.date);
					const wd = d.getUTCDay();
					if (!weekdaySums[wd]) weekdaySums[wd] = { sum: 0, count: 0 };
					if (t.individual_moods.length > 0) {
						weekdaySums[wd].sum += t.mood_average;
						weekdaySums[wd].count += 1;
					}
				});

				const weekdayAverages: Record<string, number> = {};
				const names = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
				for (let i = 0; i < 7; i++) {
					const rec = weekdaySums[i];
					weekdayAverages[names[i]] =
						rec && rec.count > 0 ? Number((rec.sum / rec.count).toFixed(2)) : 0;
				}

				// Trend direction: compare avg of first 7 vs last 7 non-empty days
				const first7 = nonEmpty.slice(0, 7);
				const last7 = nonEmpty.slice(-7);
				let trendDirection: "Improving" | "Worsening" | "Stable" = "Stable";
				if (first7.length > 0 && last7.length > 0) {
					const avgFirst =
						first7.reduce((s, t) => s + t.mood_average, 0) / first7.length;
					const avgLast =
						last7.reduce((s, t) => s + t.mood_average, 0) / last7.length;
					if (avgLast > avgFirst + 0.2) trendDirection = "Improving";
					else if (avgLast < avgFirst - 0.2) trendDirection = "Worsening";
				}

				setTrendsAnalysis({
					mostPositiveDay: mostPositiveDay
						? { date: mostPositiveDay.date, avg: mostPositiveDay.mood_average }
						: null,
					mostNegativeDay: mostNegativeDay
						? { date: mostNegativeDay.date, avg: mostNegativeDay.mood_average }
						: null,
					highestSpreadDay: highestSpreadDay
						? {
								date: highestSpreadDay.date,
								spread: highestSpreadDay.mood_spread,
							}
						: null,
					weekdayAverages,
					trendDirection,
				});
			} catch (err) {
				console.error("Error computing daily trends:", err);
				setDailyTrends([]);
			} finally {
				setTrendsLoading(false);
			}
		};

		fetchLast28Days();
	}, [users, topicId]);

	// Fetch group name when viewing topic in a group context
	useEffect(() => {
		const fetchGroup = async () => {
			if (!groupId) return;
			try {
				const { data, error } = await supabase
					.from("groups")
					.select("name")
					.eq("id", groupId)
					.maybeSingle();
				if (error) {
					console.error("Error fetching group name:", error);
					setGroupName(null);
					return;
				}
				setGroupName(data?.name || null);
			} catch (err) {
				console.error("Error fetching group name:", err);
				setGroupName(null);
			}
		};

		fetchGroup();
	}, [groupId]);

	const fetchTopicInsightsAnalysis = async () => {
		if (!topicId || !topic) return;

		try {
			setInsightsLoading(true);
			console.log(`🤖 Generating insights analysis for topic ${topicId}`);
			console.log(`📝 Topic details:`, {
				id: topic.id,
				title: topic.title,
				description: topic.description?.substring(0, 100) + "...",
			});

			// Step 1: Fetch reflections using the existing function
			console.log(`🔍 Step 1: Fetching reflections for topic...`);
			const reflections = await fetchTopicReflections();

			if (reflections.length === 0) {
				console.log(`⚠️ No reflections found for topic ${topicId}`);
				setInsightsAnalysis({
					positiveObservations: [],
					negativeObservations: [],
					impactTrends: {
						overall: "stable",
						recentTrend: "No data available",
						significantChanges: [],
					},
					signalPatterns: [],
					insights: {
						keyFindings: [
							"No reflections available for analysis",
							"Check that the topic is assigned to groups with active members",
							"Ensure reflections are tagged with this topic",
						],
						progressIndicators: [],
						concernAreas: ["Insufficient data for meaningful analysis"],
					},
					recommendations: [
						{
							action:
								"Assign this topic to active groups and encourage reflection creation",
							priority: "high",
							reasoning:
								"Without reflection data, no meaningful insights can be generated about user progress and patterns.",
							timeframe:
								"Immediate - ensure topic assignment and user engagement",
						},
					],
				});
				return;
			}

			console.log(`📊 Found ${reflections.length} reflections for analysis`);

			// Step 2: Get user IDs from reflections to fetch their check-ins
			const userIds = [...new Set(reflections.map((r) => r.user_id))];
			console.log(`👥 Users to analyze: ${userIds.length} users`);

			// Step 3: Fetch recent check-ins for these users (last 28 days)
			console.log(`🔍 Step 2: Fetching check-ins for users...`);
			const now = new Date();
			const startDate = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);

			const { data: checkIns, error: checkInError } = await supabase
				.from("daily_checkins")
				.select(`
          id,
          user_id,
          created_at,
          general_feeling,
          day_description,
          reflection_feeling,
          reflection_impact,
          reflection_reasons,
          signals_noticed
        `)
				.in("user_id", userIds)
				.gte("created_at", startDate.toISOString())
				.order("created_at", { ascending: false });

			if (checkInError) {
				console.error("❌ Error fetching check-ins:", checkInError);
				throw new Error(`Failed to fetch check-ins: ${checkInError.message}`);
			}

			console.log(`📋 Found ${checkIns?.length || 0} check-ins for analysis`);

			if (!checkIns || checkIns.length === 0) {
				console.log(`⚠️ No check-ins found for these users`);
				setInsightsAnalysis({
					positiveObservations: [],
					negativeObservations: [],
					impactTrends: {
						overall: "stable",
						recentTrend: "Insufficient check-in data",
						significantChanges: [],
					},
					signalPatterns: [],
					insights: {
						keyFindings: [
							"Reflections available but no check-in data for analysis",
							"Users need to complete daily check-ins for meaningful insights",
						],
						progressIndicators: [],
						concernAreas: [
							"No daily check-in data available for progress tracking",
						],
					},
					recommendations: [
						{
							action:
								"Encourage users to complete daily check-ins consistently",
							priority: "high",
							reasoning:
								"Daily check-ins provide the behavioral and emotional data needed to generate meaningful insights about reflection progress.",
							timeframe:
								"Ongoing - implement check-in reminders and engagement strategies",
						},
					],
				});
				return;
			}

			// Step 4: For insights, we'll analyze the first reflection as the focus point
			// and use all check-ins from users who have reflected on this topic
			console.log(`🔍 Step 3: Preparing data for AI analysis...`);
			const primaryReflection = reflections[0];

			// Format reflection data for AI service
			const reflectionData = {
				id: primaryReflection.id,
				title: topic.title,
				description: topic.description || "",
			};

			// Format check-ins data for AI service
			const formattedCheckIns = checkIns.map((checkIn) => ({
				id: checkIn.id,
				created_at: checkIn.created_at,
				reflection_feeling: checkIn.reflection_feeling,
				reflection_impact: checkIn.reflection_impact,
				reflection_reasons: checkIn.reflection_reasons,
				signals_noticed: checkIn.signals_noticed || [],
			}));

			console.log(`🤖 Calling AI service for insights analysis...`);
			console.log(`📊 Analysis data:`, {
				reflection: reflectionData,
				checkInsCount: formattedCheckIns.length,
				sampleCheckIn: formattedCheckIns[0],
			});

			// Step 5: Generate analysis using AI service
			const analysis = await aiService.analyzeReflectionProgress(
				reflectionData,
				formattedCheckIns,
			);

			console.log(`✅ Generated insights analysis`);
			console.log(`��� Analysis summary:`, {
				positiveObservations: analysis.positiveObservations?.length || 0,
				negativeObservations: analysis.negativeObservations?.length || 0,
				recommendations: analysis.recommendations?.length || 0,
				overallTrend: analysis.impactTrends?.overall,
			});

			setInsightsAnalysis(analysis);
		} catch (error) {
			console.error("Error generating topic insights analysis:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			setInsightsAnalysis({
				positiveObservations: [],
				negativeObservations: [],
				impactTrends: {
					overall: "stable",
					recentTrend: "Analysis failed",
					significantChanges: [],
				},
				signalPatterns: [],
				insights: {
					keyFindings: [
						`Error: ${errorMessage}`,
						"Please check the console for detailed debugging information",
						"Ensure the AI reflection analysis service is properly configured",
					],
					progressIndicators: [],
					concernAreas: [
						"Analysis service error - unable to generate insights",
					],
				},
				recommendations: [
					{
						action: "Check AI service configuration and try again",
						priority: "high",
						reasoning:
							"The analysis failed due to a service error. Check that the OpenAI API key is configured and the edge function is deployed.",
						timeframe: "Immediate - resolve service configuration issues",
					},
				],
			});
		} finally {
			setInsightsLoading(false);
		}
	};

	const fetchTopicReflections = async (): Promise<any[]> => {
		if (!topicId) return [];

		try {
			console.log(`🔍 Fetching reflections for topic ${topicId}`);
			console.log(`👤 Current user: ${user?.id} (${user?.email})`);

			// First, let's see what topic assignments exist for this topic
			const { data: assignments, error: assignmentError } = await supabase
				.from("topic_assignments")
				.select(`
          id,
          topic_id,
          group_id,
          mentee_id,
          groups(id, name)
        `)
				.eq("topic_id", topicId);

			if (assignmentError) {
				console.error("Error fetching topic assignments:", assignmentError);
			} else {
				console.log(`📋 Topic assignments:`, assignments);
				const groupIds =
					assignments?.map((a) => a.group_id).filter(Boolean) || [];
				console.log(`👥 Groups assigned to this topic:`, groupIds);
			}

			// Get reflections that are tagged with this topic
			const { data: reflections, error } = await supabase
				.from("reflections")
				.select(`
          id,
          title,
          description,
          qa_results,
          future_state,
          created_at,
          user_id,
          topic_tags
        `)
				.contains("topic_tags", [topicId])
				.eq("is_draft", false)
				.eq("archived", false)
				.order("created_at", { ascending: false });

			if (error) {
				console.error("Error fetching topic reflections:", error);
				console.error("Full error details:", {
					message: error.message,
					code: error.code,
					details: error.details,
					hint: error.hint,
				});

				// If it's an RLS policy error, provide helpful guidance
				if (error.message?.includes("policy") || error.code === "42501") {
					console.error(
						"🔒 This appears to be a Row Level Security (RLS) policy issue.",
					);
					console.error(
						"💡 The new RLS policies may need to be applied to your database.",
					);
					console.error(
						"💡 Please apply the migration: supabase/migrations/20240101000001_fix_reflection_rls_for_groups.sql",
					);
				}

				return [];
			}

			console.log(`📋 Found ${reflections?.length || 0} reflections for topic`);

			if (reflections && reflections.length > 0) {
				console.log(
					`📊 Reflection details:`,
					reflections.map((r) => ({
						id: r.id,
						title: r.title?.substring(0, 50) + "...",
						user_id: r.user_id,
						created_at: r.created_at,
						topic_tags: r.topic_tags,
					})),
				);
			}

			return reflections || [];
		} catch (error) {
			console.error("Error in fetchTopicReflections:", error);
			return [];
		}
	};

	useEffect(() => {
		if (topicId && user?.id) {
			fetchTopicDetails();
		}
	}, [topicId, user?.id]);

	const fetchTopicDetails = async () => {
		if (!topicId || !user?.id) return;

		try {
			// Fetch topic details
			const { data: topicData, error: topicError } = await supabase
				.from("topics")
				.select("*")
				.eq("id", topicId)
				.eq("created_by", user.id)
				.single();

			if (topicError) {
				console.error("Error fetching topic:", topicError);
				return;
			}

			if (topicData) {
				setTopic({
					id: topicData.id,
					title: topicData.name,
					description: topicData.description,
					created_at: topicData.created_at,
					prompt_creation: topicData.prompt_creation,
				});

				// Fetch topic assignments for this topic
				await fetchTopicAssignments(topicData.id);
			}
		} catch (error) {
			console.error("Error fetching topic details:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchTopicAssignments = async (topicId: string) => {
		try {
			console.log(
				`🔍 TopicDetailPage: Fetching assignments for topic ${topicId}`,
			);

			// First, check if the topic exists
			const { data: topicCheck, error: topicCheckError } = await supabase
				.from("topics")
				.select("id, name")
				.eq("id", topicId)
				.single();

			if (topicCheckError || !topicCheck) {
				console.error(`❌ Topic ${topicId} does not exist:`, topicCheckError);
				return;
			}

			console.log(`✅ Topic found: ${topicCheck.name} (${topicCheck.id})`);

			// Fetch individual mentee assignments for this topic
			// Note: Using explicit approach instead of foreign key joins for better reliability
			const { data: assignments, error: assignmentError } = await supabase
				.from("topic_assignments")
				.select(`
          id,
          mentee_id,
          group_id,
          topic_id,
          created_at
        `)
				.eq("topic_id", topicId);

			if (assignmentError) {
				console.error("Error fetching topic assignments:", assignmentError);
				return;
			}

			console.log(`📋 Raw assignments response:`, assignments);
			console.log(
				`📋 Found ${assignments?.length || 0} individual mentee assignments`,
			);

			if (assignments && assignments.length > 0) {
				// Log detailed assignment information
				assignments.forEach((assignment, index) => {
					console.log(`📋 Assignment ${index + 1}:`, {
						id: assignment.id,
						mentee_id: assignment.mentee_id,
						group_id: assignment.group_id,
					});
				});

				setTopicAssignments(assignments);

				// Collect unique group IDs from assignments
				const groupIds = Array.from(
					new Set(assignments.map((a) => a.group_id).filter(Boolean)),
				);
				console.log(
					`📊 Topic assigned to ${groupIds.length} groups:`,
					groupIds,
				);

				// Process all assignments regardless of group association
				await processAssignmentData(assignments);
			} else {
				// No assignments found - this topic isn't assigned to anyone yet
				console.log(`⚠��� Topic ${topicId} has no assignments yet`);
				console.log(`🔍 You may need to:`);
				console.log(`  1. Go to Group Management`);
				console.log(`  2. Create or select a group with members`);
				console.log(`  3. Click "Assign Topic" on the group`);
				console.log(`  4. Select this topic to assign it to the group`);
				setUsers([]);
				setTopicAssignments([]);
			}
		} catch (error) {
			console.error("Error in fetchTopicAssignments:", error);
			setUsers([]);
			setTopicAssignments([]);
		}
	};

	const processAssignmentData = async (assignments: any[]) => {
		try {
			console.log(
				`👥 TopicDetailPage: Processing ${assignments.length} assignments`,
			);

			// Extract unique mentee IDs
			const menteeIds = [
				...new Set(assignments.map((a) => a.mentee_id).filter((id) => id)),
			];
			console.log(`📋 Unique mentee IDs to fetch profiles for:`, menteeIds);

			// Check current authentication status
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();
			console.log(`🔑 Current session:`, {
				user_id: session?.user?.id,
				email: session?.user?.email,
				has_session: !!session,
				session_error: sessionError,
			});

			// Fetch profiles for all mentee IDs in bulk
			console.log(`🔍 Attempting to fetch profiles for mentee IDs:`, menteeIds);
			const { data: profiles, error: profilesError } = await supabase
				.from("profiles")
				.select("id, name, email")
				.in("id", menteeIds);

			console.log(`📋 Profile fetch result:`, {
				profiles_count: profiles?.length || 0,
				error: profilesError,
				profiles: profiles,
			});

			if (profilesError) {
				console.error(`❌ Error fetching profiles:`, profilesError);
				console.error(`❌ Error details:`, {
					message: profilesError.message,
					code: profilesError.code,
					details: profilesError.details,
					hint: profilesError.hint,
				});
				setUsers([]);
				return;
			}

			console.log(
				`👥 Fetched ${profiles?.length || 0} profiles:`,
				profiles?.map((p) => ({ id: p.id, name: p.name })),
			);

			// Create a map for quick profile lookup
			const profileMap = new Map();
			(profiles || []).forEach((profile) => {
				profileMap.set(profile.id, profile);
			});

			// Calculate stats for each assigned mentee
			const menteeData = await Promise.all(
				assignments.map(async (assignment: any) => {
					const menteeId = assignment.mentee_id;

					// Validate mentee ID format
					if (
						!menteeId ||
						typeof menteeId !== "string" ||
						menteeId.trim() === ""
					) {
						console.warn(
							`⚠️ Invalid mentee ID in assignment ${assignment.id}: ${menteeId}`,
						);
						return null;
					}

					// Check if mentee ID looks like a valid UUID
					const uuidRegex =
						/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
					if (!uuidRegex.test(menteeId)) {
						console.warn(
							`⚠️ Invalid UUID format for mentee ${menteeId} in assignment ${assignment.id}`,
						);
						return null;
					}

					// Get profile from our bulk-fetched data
					const profile = profileMap.get(menteeId);
					if (!profile) {
						console.warn(`⚠️ No profile found for mentee ${menteeId}`);
						return null;
					}

					console.log(`✅ Found profile for ${menteeId}: ${profile.name}`);

					// Get all reflections for this mentee (since topic tagging may not be used consistently)
					console.log(
						`🔍 Fetching reflections for mentee ${menteeId} (${profile.name})`,
					);
					const { data: allReflections } = await supabase
						.from("reflections")
						.select("id, created_at, topic_tags")
						.eq("user_id", menteeId)
						.eq("is_draft", false);

					// Try to find topic-specific reflections first, then fall back to all reflections
					let reflections =
						allReflections?.filter(
							(r) =>
								r.topic_tags &&
								Array.isArray(r.topic_tags) &&
								r.topic_tags.includes(topicId),
						) || [];

					if (
						reflections.length === 0 &&
						allReflections &&
						allReflections.length > 0
					) {
						reflections = allReflections;
						console.log(
							`📝 No topic-specific reflections found, using ${reflections.length} total reflections for ${profile.name}`,
						);
					} else if (reflections.length > 0) {
						console.log(
							`📝 Found ${reflections.length} topic-specific reflections for ${profile.name}`,
						);
					} else {
						console.log(`📝 No reflections found for ${profile.name}`);
					}

					// Get check-in count for last 30 days
					const thirtyDaysAgo = new Date(
						Date.now() - 30 * 24 * 60 * 60 * 1000,
					).toISOString();
					const { data: checkIns } = await supabase
						.from("daily_checkins")
						.select("id, created_at")
						.eq("user_id", menteeId)
						.gte("created_at", thirtyDaysAgo);

					// Calculate engagement level and activity category based on recent activity
					const reflectionCount = reflections?.length || 0;
					const checkInCount = checkIns?.length || 0;
					const totalActivity = reflectionCount + checkInCount;

					// Determine activity category using last 14 days distribution
					const now = new Date();
					const fourteenDaysAgo = new Date(
						now.getTime() - 14 * 24 * 60 * 60 * 1000,
					);
					const sevenDaysAgo = new Date(
						now.getTime() - 7 * 24 * 60 * 60 * 1000,
					);

					const checkInsLast14 = (checkIns || []).filter(
						(ci: any) => new Date(ci.created_at) >= fourteenDaysAgo,
					);
					const week1Count = checkInsLast14.filter(
						(ci: any) => new Date(ci.created_at) >= sevenDaysAgo,
					).length;
					const week2Count = checkInsLast14.filter(
						(ci: any) =>
							new Date(ci.created_at) >= fourteenDaysAgo &&
							new Date(ci.created_at) < sevenDaysAgo,
					).length;

					let activity: "none" | "low" | "medium" | "high" = "low";
					if (reflectionCount === 0 && checkInsLast14.length === 0) {
						activity = "none";
					} else if (
						reflectionCount >= 1 &&
						week1Count >= 3 &&
						week2Count >= 3
					) {
						activity = "high";
					} else if (reflectionCount >= 1 || checkInsLast14.length >= 3) {
						activity = "medium";
					} else {
						activity = "low";
					}

					// Calculate progress score based on recent activity
					const progressScore = Math.min(
						100,
						Math.round((totalActivity / 20) * 100),
					);

					// Get last activity date
					const lastReflection = reflections?.[0]?.created_at;
					const lastCheckIn = checkIns?.[0]?.created_at;
					const lastActivity =
						[lastReflection, lastCheckIn, assignment.created_at]
							.filter(Boolean)
							.sort(
								(a, b) => new Date(b!).getTime() - new Date(a!).getTime(),
							)[0] || assignment.created_at;

					const result: TopicUser = {
						id: menteeId,
						name: profile.name as string,
						email: profile.email as string,
						last_activity: String(lastActivity),
						reflections_count: reflectionCount,
						checkins_completed: checkInCount,
						progress_score: progressScore,
						engagement_level:
							totalActivity >= 15
								? "high"
								: totalActivity >= 5
									? "medium"
									: "low",
					};
					return result;
				}),
			);

			// Filter out null entries and remove duplicates (in case a mentee has multiple assignments)
			const validMenteeData = menteeData
				.filter((data) => data !== null)
				.filter(
					(data, index, array) =>
						array.findIndex((item) => item?.id === data?.id) === index,
				);

			console.log(
				`📊 TopicDetailPage: Processed ${assignments.length} assignments, found ${validMenteeData.length} valid mentees`,
			);
			console.log(
				`👥 Valid mentee IDs:`,
				validMenteeData.map((m) => m.id),
			);
			console.log(
				`👥 Valid mentee names:`,
				validMenteeData.map((m) => m.name),
			);

			if (validMenteeData.length === 0 && assignments.length > 0) {
				console.warn(
					`⚠��� No valid mentee profiles found despite ${assignments.length} assignments existing`,
				);
				console.warn(`⚠️ Available mentee IDs:`, menteeIds);
				console.warn(`⚠️ Fetched profiles:`, profiles?.map((p) => p.id) || []);
			}

			setUsers(validMenteeData as TopicUser[]);
		} catch (error) {
			console.error("Error processing assignment data:", error);
			setUsers([]);
		}
	};

	const fetchAssignedMentees = async (groupIds: string[]) => {
		try {
			console.log(
				`👥 TopicDetailPage: Fetching mentees from ${groupIds.length} groups:`,
				groupIds,
			);

			// Fetch group memberships for these groups
			const { data: memberships, error: membershipError } = await supabase
				.from("group_memberships")
				.select(`
          mentee_id,
          group_id,
          joined_at,
          profiles!inner(
            id,
            name,
            email
          )
        `)
				.in("group_id", groupIds);

			if (membershipError) {
				console.error("Error fetching group memberships:", membershipError);
				return;
			}

			console.log(`👥 Found ${memberships?.length || 0} group memberships`);

			if (memberships && memberships.length > 0) {
				// Calculate stats for each mentee
				const menteeData = await Promise.all(
					memberships.map(async (membership: any) => {
						const menteeId = membership.mentee_id;
						const profile = membership.profiles;

						// Get reflection count for this mentee and topic
						const { data: reflections } = await supabase
							.from("reflections")
							.select("id, created_at")
							.eq("user_id", menteeId)
							.eq("is_draft", false)
							.contains("topic_tags", [topicId]);

						// Get check-in count for last 30 days
						const thirtyDaysAgo = new Date(
							Date.now() - 30 * 24 * 60 * 60 * 1000,
						).toISOString();
						const { data: checkIns } = await supabase
							.from("daily_checkins")
							.select("id, created_at")
							.eq("user_id", menteeId)
							.gte("created_at", thirtyDaysAgo);

						// Calculate engagement level based on activity
						const reflectionCount = reflections?.length || 0;
						const checkInCount = checkIns?.length || 0;
						const totalActivity = reflectionCount + checkInCount;

						// Determine activity based on last 14 days
						const now = new Date();
						const fourteenDaysAgo = new Date(
							now.getTime() - 14 * 24 * 60 * 60 * 1000,
						);
						const sevenDaysAgo = new Date(
							now.getTime() - 7 * 24 * 60 * 60 * 1000,
						);
						const checkInsLast14 = (checkIns || []).filter(
							(ci: any) => new Date(ci.created_at) >= fourteenDaysAgo,
						);
						const week1Count = checkInsLast14.filter(
							(ci: any) => new Date(ci.created_at) >= sevenDaysAgo,
						).length;
						const week2Count = checkInsLast14.filter(
							(ci: any) =>
								new Date(ci.created_at) >= fourteenDaysAgo &&
								new Date(ci.created_at) < sevenDaysAgo,
						).length;

						let activity: "none" | "low" | "medium" | "high" = "low";
						if (reflectionCount === 0 && checkInsLast14.length === 0) {
							activity = "none";
						} else if (
							reflectionCount >= 1 &&
							week1Count >= 3 &&
							week2Count >= 3
						) {
							activity = "high";
						} else if (reflectionCount >= 1 || checkInsLast14.length >= 3) {
							activity = "medium";
						} else {
							activity = "low";
						}

						// Calculate progress score based on recent activity
						const progressScore = Math.min(
							100,
							Math.round((totalActivity / 20) * 100),
						);

						// Get last activity date
						const lastReflection = reflections?.[0]?.created_at;
						const lastCheckIn = checkIns?.[0]?.created_at;
						const lastActivity =
							[lastReflection, lastCheckIn, membership.joined_at]
								.filter(Boolean)
								.sort(
									(a, b) => new Date(b!).getTime() - new Date(a!).getTime(),
								)[0] || membership.joined_at;

						const result: TopicUser = {
							id: menteeId,
							name: profile.name as string,
							email: profile.email as string,
							last_activity: String(lastActivity),
							reflections_count: reflectionCount,
							checkins_completed: checkInCount,
							progress_score: progressScore,
							engagement_level:
								totalActivity >= 15
									? "high"
									: totalActivity >= 5
										? "medium"
										: "low",
						};
						return result;
					}),
				);

				console.log(
					`📊 TopicDetailPage: Found ${menteeData.length} mentees with IDs:`,
					menteeData.map((m) => m.id),
				);
				setUsers(menteeData as TopicUser[]);
			} else {
				setUsers([]);
			}
		} catch (error) {
			console.error("Error fetching assigned mentees:", error);
			setUsers([]);
		}
	};

	useEffect(() => {
		const fetchWeeklyStats = async () => {
			if (!topicId || users.length === 0) {
				setWeeklyStats({
					checkinsCompleted: 0,
					possibleCheckins: 0,
					newReflections: 0,
					avgMood: 0,
					stressTrend: "Stable",
				});
				return;
			}

			try {
				const userIds = users.map((u) => u.id);
				const now = new Date();
				const sevenDaysAgo = new Date(
					now.getTime() - 7 * 24 * 60 * 60 * 1000,
				).toISOString();
				const fourteenDaysAgo = new Date(
					now.getTime() - 14 * 24 * 60 * 60 * 1000,
				).toISOString();

				const { data: checkins, error: checkinError } = await supabase
					.from("daily_checkins")
					.select("id, user_id, general_feeling, created_at")
					.in("user_id", userIds)
					.gte("created_at", fourteenDaysAgo);

				if (checkinError)
					console.error("Error fetching weekly checkins:", checkinError);

				const week1 = (checkins || []).filter(
					(ci: any) => new Date(ci.created_at) >= new Date(sevenDaysAgo),
				);
				const week2 = (checkins || []).filter(
					(ci: any) =>
						new Date(ci.created_at) >= new Date(fourteenDaysAgo) &&
						new Date(ci.created_at) < new Date(sevenDaysAgo),
				);

				const checkinsCompleted = week1.length;
				const possibleCheckins = users.length * 7;
				const avgMood = week1.length
					? week1.reduce(
							(s: number, ci: any) => s + (ci.general_feeling || 0),
							0,
						) / week1.length
					: 0;

				let stressTrend: "Improving" | "Worsening" | "Stable" = "Stable";
				const avg1 = week1.length
					? week1.reduce(
							(s: number, ci: any) => s + (ci.general_feeling || 0),
							0,
						) / week1.length
					: null;
				const avg2 = week2.length
					? week2.reduce(
							(s: number, ci: any) => s + (ci.general_feeling || 0),
							0,
						) / week2.length
					: null;
				if (avg1 != null && avg2 != null) {
					if (avg1 > avg2 + 0.2) stressTrend = "Improving";
					else if (avg1 < avg2 - 0.2) stressTrend = "Worsening";
				}

				const { data: reflections, error: reflectionsError } = await supabase
					.from("reflections")
					.select("id")
					.in("user_id", userIds)
					.eq("is_draft", false)
					.eq("archived", false)
					.gte("created_at", sevenDaysAgo)
					.contains("topic_tags", [topicId]);

				if (reflectionsError)
					console.error("Error fetching weekly reflections:", reflectionsError);

				const newReflections = reflections?.length || 0;

				setWeeklyStats({
					checkinsCompleted,
					possibleCheckins,
					newReflections,
					avgMood,
					stressTrend,
				});
			} catch (error) {
				console.error("Error computing weekly stats:", error);
			}
		};

		fetchWeeklyStats();
	}, [users, topicId]);

	const getEngagementColor = (level: string) => {
		switch (level) {
			case "high":
				return "bg-green-100 text-green-800";
			case "medium":
				return "bg-yellow-100 text-yellow-800";
			case "low":
				return "bg-red-100 text-red-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	const getImpactColor = (impact: string) => {
		switch (impact) {
			case "high":
				return "text-red-600";
			case "medium":
				return "text-yellow-600";
			case "low":
				return "text-green-600";
			default:
				return "text-gray-600";
		}
	};

	const getSentimentColor = (sentiment: string) => {
		switch (sentiment) {
			case "positive":
				return "bg-green-100 text-green-800";
			case "negative":
				return "bg-red-100 text-red-800";
			case "neutral":
				return "bg-gray-100 text-gray-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	if (loading) {
		return (
			<div className="text-center py-8">
				<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
				<p className="text-muted-foreground">Loading topic details...</p>
			</div>
		);
	}

	if (!topic) {
		return (
			<div className="text-center py-8">
				<p className="text-muted-foreground">Topic not found</p>
				<Button
					onClick={() =>
						groupId
							? navigate(`/mentor/group/${groupId}`)
							: window.history.length > 1
								? navigate(-1)
								: navigate("/mentor")
					}
					className="mt-4"
				>
					<ArrowLeft className="w-4 h-4 mr-2" />
					Back to Group
				</Button>
			</div>
		);
	}

	return (
		<TooltipProvider>
			<div className="container mx-auto px-4 py-8 max-w-7xl">
				{/* Header */}
				<div className="mb-8">
					<Button
						variant="ghost"
						onClick={() =>
							groupId
								? navigate(`/mentor/group/${groupId}`)
								: window.history.length > 1
									? navigate(-1)
									: navigate("/mentor")
						}
						className="mb-4"
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Back to Group
					</Button>
					<h1 className="text-3xl font-bold mb-2">{topic.title}</h1>
					{groupName && (
						<p className="text-2xl text-muted-foreground mb-1">{groupName}</p>
					)}
					<p className="text-muted-foreground">{topic.description}</p>
				</div>

				<div className="w-full">
					{/* Structured Team Analysis Section */}
					<div className="w-full">
						{users.length > 0 ? (
							<StructuredTeamAnalysis
								topic={topic}
								groupId={groupId}
								topicId={topicId || ""}
								users={users}
								dailyTrends={dailyTrends}
								insightsAnalysis={insightsAnalysis}
								weeklyStats={weeklyStats}
								onGenerateInsights={fetchTopicInsightsAnalysis}
								insightsLoading={insightsLoading}
							/>
						) : (
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center space-x-2">
										<BarChart3 className="w-5 h-5" />
										<span>Team Analysis & Insights</span>
									</CardTitle>
									<CardDescription>
										Structured analysis and actionable intelligence for team
										development
									</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="text-center py-8">
										<h4 className="font-medium mb-2">
											No Valid Team Members Found
										</h4>
										{topicAssignments.length > 0 ? (
											<div>
												<p className="text-sm mb-4 text-yellow-600">
													⚠️ Found {topicAssignments.length} topic assignment(s),
													but no valid member profiles could be loaded.
												</p>
												<p className="text-sm mb-4">
													This usually means the mentee IDs in the assignments
													don't correspond to existing user profiles.
												</p>
												<p className="text-sm mb-4">To fix this issue:</p>
												<ol className="text-sm text-left max-w-md mx-auto space-y-1">
													<li>
														1. Check that the assigned group members have valid
														user profiles
													</li>
													<li>
														2. Verify the group memberships are properly
														configured
													</li>
													<li>
														3. Re-assign the topic to groups with valid members
													</li>
												</ol>
											</div>
										) : (
											<div>
												<p className="text-sm mb-4">
													This topic hasn't been assigned to any groups yet.
												</p>
												<p className="text-sm mb-4">
													To get group analysis data, you need to:
												</p>
												<ol className="text-sm text-left max-w-md mx-auto space-y-1">
													<li>1. Go to Group Management</li>
													<li>2. Create or select a group with members</li>
													<li>3. Click "Assign Topic" on the group</li>
													<li>
														4. Select this topic to assign it to the group
													</li>
												</ol>
											</div>
										)}
										<div className="mt-4">
											<Button
												variant="outline"
												onClick={() =>
													window.history.length > 1
														? navigate(-1)
														: navigate("/mentor")
												}
												className="text-sm"
											>
												Go to Group Management
											</Button>
										</div>
									</div>
								</CardContent>
							</Card>
						)}
					</div>
				</div>
			</div>
		</TooltipProvider>
	);
}
